import { useState, useEffect, useCallback, useMemo } from 'react';
import { BedDouble, Plus, RefreshCw, TrendingUp, Pencil, Trash2, Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
  DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel,
  AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useToast } from '@/components/ui/use-toast';
import {
  fetchClientOccupancy,
  createClientOccupancy,
  updateClientOccupancy,
  deleteClientOccupancy,
} from '@/pages/laundry/ocupacion-clientes/services/clientOccupancy.services';
import { fetchClients } from '@/pages/parametrizacion/clients/Services/clients.services.jsx';

const ITEMS_PER_PAGE = 20;
const getTodayDate = () => new Date().toISOString().split('T')[0];
const emptyForm = { habitacionesOcupacion: '', fecha: getTodayDate() };

const fmtDate = (d) =>
  d ? new Date(d).toLocaleDateString('es-CO', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '-';

const pctBadge = (pct) => {
  if (pct >= 80) return 'bg-green-100 text-green-700';
  if (pct >= 50) return 'bg-yellow-100 text-yellow-700';
  return 'bg-red-100 text-red-600';
};

const OcupacionPanel = ({ clienteId, clienteNombre }) => {
  const { toast } = useToast();

  // Datos del cliente (para obtener lavandería y habitacionesTotales)
  const [clienteData, setClienteData] = useState(null);

  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);

  const [month, setMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });

  // Modal
  const [modalOpen, setModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [isSaving, setIsSaving] = useState(false);

  // Eliminar
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);

  const totalPages = useMemo(() => Math.max(1, Math.ceil(total / ITEMS_PER_PAGE)), [total]);

  // Cargar datos del cliente (para lavandería y habitacionesTotales)
  useEffect(() => {
    if (!clienteId) return;
    fetchClients({ limit: 1, offset: 1, query: { _id: clienteId } })
      .then((res) => {
        const c = res?.data?.[0] || null;
        setClienteData(c);
      })
      .catch(() => setClienteData(null));
  }, [clienteId]);

  const buildDateRange = useCallback((monthStr) => {
    const [y, m] = monthStr.split('-').map(Number);
    const start = new Date(y, m - 1, 1, 0, 0, 0);
    const end = new Date(y, m, 0, 23, 59, 59);
    return { $gte: start.toISOString(), $lte: end.toISOString() };
  }, []);

  const loadRecords = useCallback(async (targetPage = 1) => {
    if (!clienteId) return;
    setLoading(true);
    try {
      const { data, totalRecords } = await fetchClientOccupancy({
        limit: ITEMS_PER_PAGE,
        page: targetPage,
        query: { 'cliente.id': clienteId, fecha: buildDateRange(month) },
      });
      setRecords(Array.isArray(data) ? data : []);
      setTotal(totalRecords || 0);
    } catch {
      toast({ title: 'Error', description: 'No se pudieron cargar los registros.', variant: 'destructive' });
      setRecords([]);
    } finally {
      setLoading(false);
    }
  }, [clienteId, month, buildDateRange, toast]);

  useEffect(() => {
    setPage(1);
    loadRecords(1);
  }, [month, loadRecords]);

  const openCreate = () => {
    setEditingItem(null);
    setForm(emptyForm);
    setModalOpen(true);
  };

  const openEdit = (item) => {
    setEditingItem(item);
    setForm({
      habitacionesOcupacion: String(item.habitacionesOcupacion ?? ''),
      fecha: item.fecha ? new Date(item.fecha).toISOString().split('T')[0] : getTodayDate(),
    });
    setModalOpen(true);
  };

  const handleSave = async () => {
    const habitacionesOcupacion = parseInt(form.habitacionesOcupacion);
    if (isNaN(habitacionesOcupacion) || habitacionesOcupacion < 0) {
      toast({ title: 'Error', description: 'Ingresa una cantidad válida de habitaciones.', variant: 'destructive' });
      return;
    }

    const habitacionesTotales = clienteData?.habitacionesTotales ?? 0;
    const porcentajeOcupacion = habitacionesTotales > 0
      ? parseFloat(((habitacionesOcupacion / habitacionesTotales) * 100).toFixed(2))
      : 0;

    const [year, mon, day] = form.fecha.split('-');
    const fecha = new Date(Date.UTC(+year, +mon - 1, +day, 12, 0, 0)).toISOString();

    const payload = {
      cliente: { id: clienteId, nombre: clienteNombre },
      lavanderia: clienteData?.lavanderia
        ? { id: clienteData.lavanderia.id, nombre: clienteData.lavanderia.nombre }
        : { id: '', nombre: '' },
      habitacionesOcupacion,
      habitacionesTotales,
      porcentajeOcupacion,
      fecha,
    };

    setIsSaving(true);
    try {
      if (editingItem) {
        await updateClientOccupancy(editingItem._id, payload);
        toast({ title: 'Éxito', description: 'Registro actualizado.' });
      } else {
        await createClientOccupancy(payload);
        toast({ title: 'Éxito', description: 'Ocupación registrada.' });
      }
      setModalOpen(false);
      setForm(emptyForm);
      loadRecords(1);
    } catch (error) {
      toast({ title: 'Error', description: error.message || 'No se pudo guardar.', variant: 'destructive' });
    } finally {
      setIsSaving(false);
    }
  };

  const confirmDelete = async () => {
    try {
      await deleteClientOccupancy(itemToDelete._id);
      toast({ title: 'Éxito', description: 'Registro eliminado.' });
      loadRecords(1);
    } catch {
      toast({ title: 'Error', description: 'No se pudo eliminar.', variant: 'destructive' });
    } finally {
      setDeleteOpen(false);
      setItemToDelete(null);
    }
  };

  const avgPct = records.length > 0
    ? (records.reduce((a, r) => a + (r.porcentajeOcupacion ?? 0), 0) / records.length).toFixed(1)
    : null;

  const liveHab = form.habitacionesOcupacion !== '' && (clienteData?.habitacionesTotales ?? 0) > 0
    ? ((parseInt(form.habitacionesOcupacion) / clienteData.habitacionesTotales) * 100).toFixed(1)
    : null;

  return (
    <>
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-start justify-between gap-3">
            <div>
              <CardTitle className="text-base flex items-center gap-2">
                <BedDouble className="h-5 w-5 text-teal-600" />
                Ocupación de Habitaciones
              </CardTitle>
              <CardDescription className="mt-0.5">
                Registro diario de ocupación de habitaciones
              </CardDescription>
            </div>
            <div className="flex gap-2 shrink-0">
              <Button variant="outline" size="sm" onClick={() => loadRecords(page)} disabled={loading}>
                <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              </Button>
              <Button size="sm" onClick={openCreate}
                className="bg-teal-600 hover:bg-teal-700 text-white">
                <Plus className="h-4 w-4 mr-1" /> Registrar
              </Button>
            </div>
          </div>
        </CardHeader>

        <CardContent className="space-y-4">
          {/* Filtro mes + resumen */}
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2">
              <label className="text-xs text-gray-500 font-medium">Mes:</label>
              <Input
                type="month"
                value={month}
                onChange={(e) => setMonth(e.target.value)}
                className="h-8 text-sm w-36"
              />
            </div>
            {avgPct !== null && (
              <div className="flex items-center gap-1.5 bg-teal-50 border border-teal-100 rounded-full px-3 py-1">
                <TrendingUp className="h-3.5 w-3.5 text-teal-600" />
                <span className="text-xs font-semibold text-teal-700">Promedio: {avgPct}%</span>
              </div>
            )}
            {clienteData?.habitacionesTotales > 0 && (
              <span className="text-xs text-gray-400">
                {clienteData.habitacionesTotales} hab. totales configuradas
              </span>
            )}
          </div>

          {/* Tabla */}
          {loading ? (
            <div className="text-center py-10">
              <div className="animate-spin rounded-full h-7 w-7 border-b-2 border-teal-600 mx-auto" />
              <p className="text-sm text-gray-400 mt-2">Cargando registros...</p>
            </div>
          ) : records.length === 0 ? (
            <div className="text-center py-10">
              <BedDouble className="h-10 w-10 text-gray-200 mx-auto mb-2" />
              <p className="text-sm text-gray-500 font-medium">Sin registros para este mes</p>
              <p className="text-xs text-gray-400 mt-1">Haz clic en "Registrar" para agregar la ocupación del día</p>
            </div>
          ) : (
            <>
              {/* Cabecera — solo desktop */}
              <div className="hidden sm:grid grid-cols-5 gap-2 px-3 py-1.5 bg-gray-50 rounded-lg text-xs font-semibold text-gray-500 uppercase tracking-wide">
                <span>Fecha</span>
                <span className="text-right">Ocupadas</span>
                <span className="text-right">Totales</span>
                <span className="text-right">% Ocup.</span>
                <span className="text-right">Acciones</span>
              </div>

              <div className="space-y-1">
                {records.map((r) => (
                  <div key={r._id} className="rounded-xl border bg-white px-3 py-2.5 hover:bg-gray-50 transition-colors">
                    {/* Mobile */}
                    <div className="flex items-center justify-between sm:hidden">
                      <div>
                        <p className="text-sm font-medium">{fmtDate(r.fecha)}</p>
                        <p className="text-xs text-gray-400">
                          {r.habitacionesOcupacion ?? 0} / {r.habitacionesTotales ?? 0} hab.
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge className={`text-xs ${pctBadge(r.porcentajeOcupacion ?? 0)}`}>
                          {(r.porcentajeOcupacion ?? 0).toFixed(1)}%
                        </Badge>
                        <button onClick={() => openEdit(r)} className="text-blue-500 hover:text-blue-700 p-1">
                          <Pencil className="h-3.5 w-3.5" />
                        </button>
                        <button
                          onClick={() => { setItemToDelete(r); setDeleteOpen(true); }}
                          className="text-red-400 hover:text-red-600 p-1"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </div>

                    {/* Desktop */}
                    <div className="hidden sm:grid grid-cols-5 gap-2 items-center text-sm">
                      <span className="font-medium">{fmtDate(r.fecha)}</span>
                      <span className="text-right">{r.habitacionesOcupacion ?? 0}</span>
                      <span className="text-right text-gray-500">{r.habitacionesTotales ?? 0}</span>
                      <span className="text-right">
                        <Badge className={`text-xs ${pctBadge(r.porcentajeOcupacion ?? 0)}`}>
                          {(r.porcentajeOcupacion ?? 0).toFixed(1)}%
                        </Badge>
                      </span>
                      <div className="flex justify-end gap-2">
                        <button onClick={() => openEdit(r)} className="text-blue-500 hover:text-blue-700 p-1">
                          <Pencil className="h-3.5 w-3.5" />
                        </button>
                        <button
                          onClick={() => { setItemToDelete(r); setDeleteOpen(true); }}
                          className="text-red-400 hover:text-red-600 p-1"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Paginación */}
              {total > ITEMS_PER_PAGE && (
                <div className="flex items-center justify-between pt-1">
                  <span className="text-xs text-gray-400">
                    Pág. {page} de {totalPages} · {total} registros
                  </span>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm"
                      disabled={loading || page <= 1}
                      onClick={() => { const p = page - 1; setPage(p); loadRecords(p); }}>
                      Anterior
                    </Button>
                    <Button variant="outline" size="sm"
                      disabled={loading || page >= totalPages}
                      onClick={() => { const p = page + 1; setPage(p); loadRecords(p); }}>
                      Siguiente
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Modal registrar/editar */}
      <Dialog open={modalOpen} onOpenChange={(open) => { setModalOpen(open); if (!open) setForm(emptyForm); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <BedDouble className="h-5 w-5 text-teal-600" />
              {editingItem ? 'Editar ocupación' : 'Registrar ocupación'}
            </DialogTitle>
            <DialogDescription>
              {editingItem ? 'Modifica los datos del registro seleccionado.' : 'Ingresa la ocupación de habitaciones para hoy.'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-1">
            <div>
              <label className="text-xs font-medium text-gray-600 block mb-1">Fecha</label>
              <Input
                type="date"
                value={form.fecha}
                max={getTodayDate()}
                onChange={(e) => setForm((p) => ({ ...p, fecha: e.target.value }))}
              />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600 block mb-1">
                Habitaciones ocupadas
              </label>
              <Input
                type="number"
                min="0"
                placeholder="0"
                value={form.habitacionesOcupacion}
                onChange={(e) => setForm((p) => ({ ...p, habitacionesOcupacion: e.target.value }))}
              />
            </div>

            {/* Preview calculado */}
            {(clienteData?.habitacionesTotales ?? 0) > 0 && (
              <div className="rounded-lg bg-teal-50 border border-teal-100 px-3 py-2.5 space-y-1">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Hab. totales:</span>
                  <span className="font-medium">{clienteData.habitacionesTotales}</span>
                </div>
                {liveHab !== null && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">% Ocupación:</span>
                    <span className={`font-bold ${+liveHab >= 80 ? 'text-green-600' : +liveHab >= 50 ? 'text-yellow-600' : 'text-red-500'}`}>
                      {liveHab}%
                    </span>
                  </div>
                )}
              </div>
            )}

            {clienteData && !(clienteData?.habitacionesTotales > 0) && (
              <p className="text-xs text-amber-600 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2">
                ⚠ El cliente no tiene habitaciones totales configuradas. Contacta al administrador.
              </p>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => { setModalOpen(false); setForm(emptyForm); }}>
              Cancelar
            </Button>
            <Button
              onClick={handleSave}
              disabled={isSaving}
              className="bg-teal-600 hover:bg-teal-700 text-white"
            >
              {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {editingItem ? 'Actualizar' : 'Registrar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirmar eliminar */}
      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar registro?</AlertDialogTitle>
            <AlertDialogDescription>
              Se eliminará el registro del{' '}
              <strong>{itemToDelete?.fecha ? fmtDate(itemToDelete.fecha) : ''}</strong>.
              Esta acción no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete}>Eliminar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default OcupacionPanel;
